import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useAuth } from "./AuthContext";
import { fetchAPI } from "../lib/utils";

const NotificationCountsContext = createContext({
	messageUnreadCount: 0,
	feedbackUnreadCount: 0,
	refreshMessageUnreadCount: async () => {},
	refreshFeedbackUnreadCount: async () => {},
	refreshAllNotificationCounts: async () => {},
});

export function NotificationCountsProvider({ children }) {
	const { user } = useAuth();
	const isAdmin = user?.is_admin || user?.role === "admin";
	const isCoachOrAdmin = user?.role === "coach" || isAdmin;
	const [messageUnreadCount, setMessageUnreadCount] = useState(0);
	const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);

	const refreshMessageUnreadCount = useCallback(async () => {
		if (!user?.user_id) {
			setMessageUnreadCount(0);
			return 0;
		}
		try {
			const data = await fetchAPI("/messages/unread-count");
			const nextCount = Math.max(0, Number(data?.count) || 0);
			setMessageUnreadCount(nextCount);
			return nextCount;
		} catch {
			return 0;
		}
	}, [user?.user_id]);

	const refreshFeedbackUnreadCount = useCallback(async () => {
		if (!isCoachOrAdmin) {
			setFeedbackUnreadCount(0);
			return 0;
		}
		try {
			const data = await fetchAPI("/coach/feedback/unread-count");
			const nextCount = Math.min(99, Math.max(0, Number(data?.count) || 0));
			setFeedbackUnreadCount(nextCount);
			return nextCount;
		} catch {
			return 0;
		}
	}, [isCoachOrAdmin]);

	const refreshAllNotificationCounts = useCallback(async () => {
		await refreshMessageUnreadCount();
		await refreshFeedbackUnreadCount();
	}, [refreshFeedbackUnreadCount, refreshMessageUnreadCount]);

	useEffect(() => {
		if (!user?.user_id) {
			setMessageUnreadCount(0);
			setFeedbackUnreadCount(0);
			return;
		}

		refreshAllNotificationCounts();

		const refreshVisibleCounts = () => {
			if (document.visibilityState === "visible") {
				refreshAllNotificationCounts();
			}
		};
		// Poll on a 60s cadence (kept low to spare the DB operation budget); a
		// tab becoming visible triggers an immediate refresh, so focus is covered
		// without a second, redundant listener. Hidden tabs skip the poll: the
		// badge is not visible there and the refresh on visibility catches up.
		const intervalId = window.setInterval(() => {
			if (document.visibilityState === "visible") {
				refreshAllNotificationCounts();
			}
		}, 60000);
		document.addEventListener("visibilitychange", refreshVisibleCounts);

		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", refreshVisibleCounts);
		};
	}, [refreshAllNotificationCounts, user?.user_id]);

	const value = useMemo(
		() => ({
			messageUnreadCount,
			feedbackUnreadCount,
			refreshMessageUnreadCount,
			refreshFeedbackUnreadCount,
			refreshAllNotificationCounts,
		}),
		[
			feedbackUnreadCount,
			messageUnreadCount,
			refreshAllNotificationCounts,
			refreshFeedbackUnreadCount,
			refreshMessageUnreadCount,
		],
	);

	return (
		<NotificationCountsContext.Provider value={value}>
			{children}
		</NotificationCountsContext.Provider>
	);
}

export function useNotificationCounts() {
	return useContext(NotificationCountsContext);
}
